import { Button, FormControlLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material"
import { Controller, useForm, UseFormTrigger } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";

interface FormData { [key: string]: any;}
interface PlateNumberProps {
    setValue?: (field: string, value: any) => void;
    setOpenPlateNumberDetails?: any;
    plate?: any;
    region?: number;
    setIsBtnEnabled?: any;
    vehiclePlateFormat: any[];
    trigger: UseFormTrigger<FormData>
}
export default function PlateNumberModal(props: PlateNumberProps) {
    const { t } = useTranslation();
    const loggedInUserData = useSelector(selectUserInfo);
    const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
    const isArabicLanguage = (lang?: string) => {
        const l = (lang || "").toLowerCase();
        return l === "arabic" || l === "ar" || l.includes("arab");
    };
    const localizedPlateNumberPlaceholder = (lang?: string) =>
        isArabicLanguage(lang) ? t("phPlateNumberAr") : t("phPlateNumberEn");
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const { register, control, trigger, formState: { errors }, watch, resetField, getValues } = 
        useForm<FormData>({
            defaultValues: (() => {
                const plateRegion = props?.region || 1;
                const values: any = { plateRegion };

                if (plateRegion === 1 && props?.plate) {
                const parts = props.plate.split(' ');
                props.vehiclePlateFormat?.forEach((item, index) => {
                    const key = item.title?.toLowerCase().replace(/\s+/g, '') + index;
                    values[key] = parts[index] || '';
                });
                } else {
                values.code = props?.plate || '';
                }

                return values;
            })(),
            mode: 'onChange',
            reValidateMode: 'onBlur'
    });
    const submission = (data: any) => {
        const plateRegion = watch('plateRegion');
        let fullPlateNumber = '';
        if (plateRegion === 1) {
            const parts = props.vehiclePlateFormat.map((item, index) => {
                const key = item.title?.toLowerCase().replace(/\s+/g, '') + index;
                return data?.[key]?.toString().trim();
            });
            fullPlateNumber = parts.join(' ');
        } else fullPlateNumber = data.code;
        props?.setValue?.('plateNumber', fullPlateNumber);
        props?.setValue?.('plateRegion', data.plateRegion);
        props.trigger('plateNumber');
        props?.setOpenPlateNumberDetails(false); props?.setIsBtnEnabled(true);
    };
    return (
        <Grid container component="form" id="form" className="items-baseline" columnSpacing={3}>
            <Grid item xs={12}>
                <Controller
                    name="plateRegion"
                    control={control}
                    render={({ field }) => (<>
                        <label className="text-left block mb-2">{t('select') + ' ' + t('plateRegion') + ':'}</label>
                        <RadioGroup row aria-labelledby="demo-row-radio-buttons-group-label" {...field} onChange={(e: any) => {
                            const value = parseInt(e.target.value, 10);
                            field.onChange(value);
                            if (value === 1) { resetField('number'); }
                        }}>
                            <FormControlLabel value={1} control={<Radio sx={{ '& svg:nth-child(2) path': { fill: themeColors?.primary } }} />} label={t('local')} sx={{ '& .MuiTypography-root': { position: 'relative', top: '-8px' }, }} />
                            <FormControlLabel value={2} control={<Radio sx={{ '& svg:nth-child(2) path': { fill: themeColors?.primary } }} />} label={t('generic')} sx={{ '& .MuiTypography-root': { position: 'relative', top: '-8px', whiteSpace: 'nowrap', width: '100px', }, }} />
                        </RadioGroup>
                    </>)}
                />
            </Grid>
            {watch('plateRegion') === 1 ? (props.vehiclePlateFormat || [])?.map((item: any, index: number) => {
                const fieldName = item?.title?.toLowerCase().replace(/\s+/g, '') + index;
                const numberMin = Math.pow(10, item.min - 1);
                const numberMax = Math.pow(10, item.max) - 1;
                return (
                    <Grid key={index} item xs={12} md={4}>
                        <TextField
                            type='text'
                            label={`${t(item?.title)} *`}
                            placeholder={localizedPlateNumberPlaceholder(primaryLanguage)}
                            title={(`${t(item?.title)} *`).replace(/\s+/g, ' ').trim()}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={!!errors?.[fieldName]}
                            helperText={errors?.[fieldName]?.message as string}
                            {...register(fieldName, {
                                required: t("This field is required."),
                                ...(item.type === 'text' && {
                                    minLength: { value: item.min, message: t(`minChars`) + ` ${item.min}` },
                                    maxLength: { value: item.max, message: t(`maxChars`) + ` ${item.max}` },
                                }),
                                ...(item.type === 'number' && {
                                    validate: value => {
                                        const normalized = value.replace(/[٠-٩]/g, (d:any) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString());
                                        if (isNaN(Number(normalized))) {
                                            return t('onlyNumericValuesAllowed');
                                        }
                                        if (normalized < numberMin || normalized > numberMax) {
                                            return t(`valueMustBeBetween`) + ` ${numberMin} & ${numberMax}`;
                                        }
                                        return true;
                                    }
                                }),
                            })}
                            onChange={(e) => {
                                (e.target.value = e.target.value.replace(/[^\p{L}\p{N}]/gu, ""));
                            }}
                        />
                    </Grid>
                );
            }) : <Grid item xs={12} md={4}>
                <TextField 
                    {...register("code", { required: t("This field is required.") })}
                    label={t('plateNumber') + ' *'}
                    placeholder={localizedPlateNumberPlaceholder(primaryLanguage)}
                    title={t('plateNumber')}
                    fullWidth
                    error={!!errors.code} 
                    helperText={errors.code?.message as string} 
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => (e.target.value = e.target.value.replace(/[^\p{L}\p{N} ]/gu, ""))}
                />
            </Grid>}
            <Grid item xs={12}>
                <div className="flex gap-2 justify-end mt-5">
                    <Button className="primary" style={{ background: themeColors?.primary }} variant="contained" disableElevation onClick={async() => {
                        const isValid = await trigger();
                        if(isValid) submission(getValues());
                    }}>{t('submit')}</Button>
                </div>
            </Grid>
        </Grid>
    )
}